import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { Button } from '@/shared/ui/button';

interface CookieBannerProps {
  content?: string;
  lang?: string;
  onAccept?: () => void;
  onDecline?: () => void;
}

const CookieBanner = ({ content, lang, onAccept, onDecline }: CookieBannerProps) => {
  const { t } = useTranslation('translation', { lng: lang });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already accepted/declined cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
    onAccept?.();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
    onDecline?.();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 md:bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 pb-20 md:pb-4"
          style={{ bottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="bg-card border border-border rounded-xl shadow-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex items-start gap-2.5 sm:gap-3 flex-1">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Cookie className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base mb-1">{t('cookieBanner.title')}</h3>
                  {content ? (
                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-3 sm:line-clamp-none">{content}</p>
                  ) : (
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {t('cookieBanner.description')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 w-full sm:w-auto shrink-0">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDecline}
                  className="flex-1 sm:flex-initial h-9 text-xs sm:text-sm"
                  aria-label={t('cookieBanner.decline')}
                >
                  {t('cookieBanner.decline')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleAccept}
                  className="flex-1 sm:flex-initial h-9 text-xs sm:text-sm bg-emerald-600 hover:bg-emerald-700"
                  aria-label={t('cookieBanner.accept')}
                >
                  {t('cookieBanner.accept')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDecline}
                  className="h-9 w-9 shrink-0 hidden sm:flex"
                  aria-label={t('cookieBanner.close')}
                >
                  <X className="h-4 w-4" />
                </Button>
                </div>
                <a
                  href="https://restroos.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                >
                  Powered by restroos.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;

